import { cloneElement, isValidElement, type MouseEvent, type PointerEvent } from "react";
import { Tooltip as BaseTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";
import { cn } from "../../../lib/utils";
import type { TooltipProps } from "./types";

// Radix positions TooltipContent off the trigger's own bounding box, so the trigger
// must stay the actual visible/positioned element rather than a wrapping <span> - a
// wrapper around an absolutely-positioned child (e.g. a schedule event block) collapses
// to a zero-size box in the wrong place, which makes the tooltip anchor to nowhere.
// Cloning the props onto the real trigger element (instead of wrapping it) keeps the
// trigger's box identical to what's on screen.
function Tooltip({ trigger, children, open, side = "top", align = "start", triggerClassName, contentClassName }: TooltipProps) {
    const stopPropagation = (e: MouseEvent | PointerEvent) => e.stopPropagation();

    const triggerElement = isValidElement<any>(trigger)
        ? cloneElement(trigger, {
            onPointerDown: (e: PointerEvent) => {
                trigger.props.onPointerDown?.(e);
                stopPropagation(e);
            },
            onClick: (e: MouseEvent) => {
                trigger.props.onClick?.(e);
                stopPropagation(e);
            },
            className: cn(trigger.props.className, triggerClassName),
        })
        : (
            <span onPointerDown={stopPropagation} onClick={stopPropagation} className={triggerClassName}>
                {trigger}
            </span>
        );

    return (
        <TooltipProvider delayDuration={100}>
            <BaseTooltip open={open}>
                <TooltipTrigger asChild>
                    {triggerElement}
                </TooltipTrigger>
                <TooltipContent
                    side={side}
                    align={align}
                    className={cn("bg-primary", contentClassName)}
                >
                    {children}
                </TooltipContent>
            </BaseTooltip>
        </TooltipProvider>
    );
}

export default Tooltip;
